import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Switch } from 'components/Switch'
import { TooltipProps } from 'components/Tooltip'
import {
  DomainProductsAttributes,
  DomainTableSchema,
} from 'features/inventory/domain/forms/DomainTableForm/schema'
import { isUnfilledRecoveryConfigured } from 'features/inventory/domain/forms/UnfilledRecoveryForm'
import { useAbility } from 'providers/casl'
import { ProductSwitchCellProps } from './types'

export const ProductSwitchCell = ({
  disabled,
  getValue,
  product,
  row: {
    original: {
      domainUnfilledRecovery: { baseAdUnitPath },
      id,
    },
  },
}: ProductSwitchCellProps) => {
  const ability = useAbility()
  const { t: tDomain } = useTranslation('features/domain')
  const { t: tProduct } = useTranslation('features/product')
  const { control, setValue } = useFormContext<DomainTableSchema>()

  const field = `${product}Enabled` as keyof DomainProductsAttributes

  const formValue = useWatch({
    control,
    name: `domainsAttributes.${id}.${field}`,
  })

  const checked: boolean = formValue ?? getValue()
  const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)
  const isUnfilledRecoveryNotConfigured: boolean = !isUnfilledRecoveryConfigured(baseAdUnitPath)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value: boolean = event.target.checked

    setValue(`domainsAttributes.${id}.${field}`, value, {
      shouldDirty: true,
    })
  }

  const composeValue = (): boolean => {
    if (noProductAccess) {
      return false
    } else if (product === 'unfilledRecovery' && isUnfilledRecoveryNotConfigured) {
      return false
    } else {
      return checked
    }
  }

  const composeDisabled = (): boolean => {
    if (product === 'unfilledRecovery') {
      return disabled || isUnfilledRecoveryNotConfigured
    } else {
      return disabled
    }
  }

  const composeTooltip = (): TooltipProps | undefined => {
    if (noProductAccess) {
      return {
        content: tProduct('featureUnavailable'),
      }
    } else if (product === 'unfilledRecovery' && isUnfilledRecoveryNotConfigured) {
      return {
        content: tDomain('form.unfilledRecovery.enabled.tooltip.notConfigured'),
      }
    } else {
      return undefined
    }
  }

  return (
    <Switch
      checked={composeValue()}
      disabled={composeDisabled()}
      name={`domainsAttributes.${id}.${field}`}
      onChange={handleChange}
      {...{
        tooltip: composeTooltip(),
        tooltipMode: 'wrap',
      }}
    />
  )
}
